import { useEffect } from 'react';
import classNames from 'classnames/bind';
import Card, { CardHeader } from '../../../components/Card';
import styles from './Summary.module.scss';
import {
  AccountAdminData,
  ANNUAL_REVIEW_DATE_TITLES,
  EntityInfoFormData,
} from '../../../state/enrollment/enrollmentModel';
import { getEdgarStatus } from '../../../state/enrollment/enrollmentActions';
import { AccountAdminStep } from '../AccountAdmin/AccountAdmin';
import Button from '../../../components/Button';

const cx = classNames.bind(styles);

const cardSummaryHeaderClasses = {
  wrapper: cx('summary__card-title-wrapper'),
  title: cx('summary__card-title'),
};

interface SummaryProps {
  isLoading: boolean;
  isEdgarAvailable: boolean;
  isDFINAdmin: boolean;
  entityInfo: EntityInfoFormData;
  accountAdminOne: AccountAdminData;
  accountAdminTwo: AccountAdminData;
  isBulk?: boolean;
  onSubmit?: () => void;
  getEdgarStatus: typeof getEdgarStatus;
}

const Summary = (props: SummaryProps) => {
  const {
    isLoading,
    isEdgarAvailable,
    isDFINAdmin,
    entityInfo,
    accountAdminOne,
    accountAdminTwo,
    isBulk,
    onSubmit,
    getEdgarStatus,
  } = props;

  useEffect(() => {
    getEdgarStatus();

    const pollInterval = setInterval(() => {
      getEdgarStatus();
    }, 60000);

    return () => clearInterval(pollInterval);
  }, []);

  const tooltipText = isEdgarAvailable
    ? undefined
    : "The SEC's EDGAR system is closed, please return to submit your enrollment during open hours 6:00 a.m. ET - 10 a.m. ET Monday-Friday.";

  const renderEntityInfo = () => {
    if (isBulk && entityInfo.names) {
      return (
        <>
          <div className={cx('summary__description')}>
            Bulk upload ({entityInfo.names?.length} entities added)
          </div>
          <div className={cx('summary__description', 'summary__list')}>
            {entityInfo.names.map((entityName: string, i: number) => (
              <div key={i}>{entityName}</div>
            ))}
          </div>
        </>
      );
    }
    return (
      <>
        <div className={cx('summary__description')}>
          Annual review date -{' '}
          {ANNUAL_REVIEW_DATE_TITLES[entityInfo.reviewDate]}
        </div>
        <div className={cx('summary__description')}>CIK - {entityInfo.cik}</div>
        <div className={cx('summary__description')}>CCC - {entityInfo.ccc}</div>
        <div className={cx('summary__description')}>
          Passphrase - {entityInfo.passphrase}
        </div>
      </>
    );
  };

  const renderAccountAdminData = (
    accountAdmin: AccountAdminData,
    step: AccountAdminStep
  ) => (
    <div>
      <div className={cx('summary__title')}>
        ACCOUNT ADMIN {step === AccountAdminStep.ONE ? '1' : '2'}
      </div>
      <div className={cx('summary__description')}>
        Email address - {accountAdmin.email}
      </div>
      <div className={cx('summary__description')}>
        First name - {accountAdmin.firstName}
      </div>
      <div className={cx('summary__description')}>
        Last name - {accountAdmin.lastName}
      </div>
      <div className={cx('summary__description')}>
        Phone number - {accountAdmin.phoneNumber}
      </div>
    </div>
  );

  return (
    <Card className={cx('summary__card')}>
      <CardHeader
        title={isBulk ? 'Review' : 'Summary'}
        classes={cardSummaryHeaderClasses}
        withoutLogo
      />
      <div className={cx('summary')}>
        {!isBulk && (
          <div>
            <div className={cx('summary__title')}>PATH SELECTION</div>
            <div className={cx('summary__description')}>
              {isDFINAdmin ? 'Let DFIN Assist' : "I'll Admin"}
            </div>
          </div>
        )}
        <div>
          <div className={cx('summary__title')}>ENTITY INFORMATION</div>
          {renderEntityInfo()}
        </div>
        {!isBulk &&
          accountAdminOne.email &&
          renderAccountAdminData(accountAdminOne, AccountAdminStep.ONE)}
        {!isBulk &&
          accountAdminTwo.email &&
          renderAccountAdminData(accountAdminTwo, AccountAdminStep.TWO)}
      </div>
      {onSubmit && (
        <div className={cx('summary__footer')}>
          <Button
            className={cx('summary__button')}
            onClick={() => onSubmit()}
            disabled={isLoading || !isEdgarAvailable}
            isLoading={isLoading}
            tooltip={tooltipText}
          >
            Submit
          </Button>
        </div>
      )}
    </Card>
  );
};

export default Summary;
